import React, { useLayoutEffect, useMemo, useEffect, useState } from "react";
import { Labelbox, Table, Button } from "../../components";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  AddCircleOutlineOutlined,
  CloudUploadOutlined,
  GetAppOutlined,
} from "@material-ui/icons";
import { read, utils, write } from "xlsx";
import "./timesheet.scss";
import {
  buildTimesheetSampleWorkbook,
  isOthersActivityOption,
  normalizeLeaveHoursForImport,
} from "../../utils/timesheetExcelTemplate";
import {
  CLIENT_ACTION,
  EMPLOYEE_ACTION,
  MASTER_ACTION,
  TIMESHEET_ACTION,
} from "../../redux/action";
import {
  Toaster,
  dropdownName,
  normalizeTimeHHmm,
  parseHoursFromExcel,
} from "../../utils/common";
import ValidationLibrary from "../../utils/validationfunction";
import {
  compareDateOnly,
  formatDateOnlyYmd,
  getEndOfCurrentWeekYmd,
  getTimesheetPeriodRange,
  getTimesheetPeriodPickerMinDate,
  parseDateFromExcel,
} from "../../utils/dateOnly";
import moment from "moment";
import {
  normalizeActivityOption,
  normalizeSubActivityOption,
  normalizeLabel,
  normalizeLabelForMatch,
  getActivityCanonicalId,
  normalizeBilledType,
  isLeaveBilledType,
  isTimesheetTotalBelowMinimum,
  timesheetMinimumHoursForHolidays,
} from "../../utils/timesheetHelpers";

const resolveActivityIdFromLabel = (label, activityList) => {
  const key = normalizeLabelForMatch(label);
  const keySpaced = normalizeLabel(label);
  if (!key && !keySpaced) return "";
  for (const act of activityList || []) {
    const actLabel = String(act.value || act.activity || "").trim();
    const actKey = normalizeLabelForMatch(actLabel);
    const actKeySpaced = normalizeLabel(actLabel);
    if (actKey === key || actKeySpaced === keySpaced) {
      return getActivityCanonicalId(act);
    }
    if (actKey && key && (actKey.includes(key) || key.includes(actKey))) {
      return getActivityCanonicalId(act);
    }
  }
  return "";
};

const parseBilledTypeFromExcel = (raw) => {
  const norm = String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/[-\s_]/g, "");
  if (norm === "leave") return "Leave";
  if (norm === "billable") return "Billable";
  if (
    norm.startsWith("nonbill") ||
    norm.includes("nonbill") ||
    norm === "idle"
  ) {
    return "Non-billable";
  }
  return "Billable";
};

const findSubActivityMatch = (subs, subActivityLabel) => {
  const key = normalizeLabelForMatch(subActivityLabel);
  const keySpaced = normalizeLabel(subActivityLabel);
  return (subs || []).find((s) => {
    const label = String(s.sub_activity || s.value || "").trim();
    const subKey = normalizeLabelForMatch(label);
    const subKeySpaced = normalizeLabel(label);
    return (
      subKey === key ||
      subKeySpaced === keySpaced ||
      (subKey && key && (subKey.includes(key) || key.includes(subKey)))
    );
  });
};

const coerceSelectOptionId = (
  value,
  options,
  idKeys = ["id", "activity_id", "sub_activity_id"],
) => {
  if (value === null || value === undefined || value === "") return "";
  const valueStr = String(value);
  for (const opt of options || []) {
    for (const key of idKeys) {
      if (opt?.[key] !== undefined && String(opt[key]) === valueStr) {
        return String(opt[key]);
      }
    }
  }
  return valueStr;
};

const CreatePage = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const submitValidateRef = React.createRef();
  const cancelValidateRef = React.createRef();
  const viewModeRef = React.useRef();
  const statusMode = React.useRef();
  const excelInputRef = React.useRef(null);
  const subActivityCacheRef = React.useRef({});
  const [tableUpdateKey, setTableUpdateKey] = React.useState(0);
  // Per-row sub-activity options (state so table re-renders with correct dropdown when activity changes)
  const [subActivityOptionsByRow, setSubActivityOptionsByRow] = React.useState(
    [],
  );
  const [clientInputModeByRow, setClientInputModeByRow] = React.useState([]);

  const defaultValues = {
    start_date: {
      value: "",
      // validation: [{ name: "required" }],
      error: null,
      errmsg: null,
      periodMinDate: null,
      periodMaxDate: null,
      customDateInput: "",
    },
    end_date: {
      value: "",
    },
    total_hours: {
      value: "00:00",
    },
    rejected_comments: {
      value: "",
    },
    itemDetails: [
      {
        date: "",
        billed_type: "Billable",
        client_id: "",
        business_name: "",
        activity_id: "",
        sub_activity_id: "",
        description: "",
        hours: "",
        custom_sub_activity: "", // For storing custom text when "Others" is selected
      },
    ],
  };

  const headers = [
    { id: "1", label: "Date" },
    { id: "2", label: "Billed Type" },
    { id: "3", label: "Client" },
    { id: "4", label: "Business Name" },
    { id: "5", label: "Activity" },
    { id: "6", label: "Sub Activity" },
    { id: "7", label: "Description" },
    { id: "8", label: "Hours" },
  ];

  const billedTypeOptions = [
    { id: "Billable", value: "Billable" },
    { id: "Non-billable", value: "Non-billable" },
    { id: "Leave", value: "Leave" },
  ];

  const [fieldConfiguration, setFieldConfiguration] =
    React.useState(defaultValues);

  const isDateRangeSelected =
    !!fieldConfiguration.start_date.value &&
    !!fieldConfiguration.end_date.value;
  const [holidayDates, setHolidayDates] = React.useState([]);
  const holidayDateSet = React.useMemo(
    () => new Set(holidayDates.map((holiday) => holiday.date)),
    [holidayDates],
  );
  const requiredTimesheetHours = timesheetMinimumHoursForHolidays(
    holidayDates.length,
  );

  React.useEffect(() => {
    const start =
      fieldConfiguration.start_date.periodMinDate ||
      fieldConfiguration.start_date.value;
    const end =
      fieldConfiguration.start_date.periodMaxDate ||
      fieldConfiguration.end_date.value;
    if (!start || !end) {
      setHolidayDates([]);
      return undefined;
    }
    let cancelled = false;
    TIMESHEET_ACTION.getTimesheetHolidays({
      start_date: start,
      end_date: end,
      employee_id: localStorage.getItem("user_id") || "",
    }).then((rows) => {
      if (!cancelled) setHolidayDates(Array.isArray(rows) ? rows : []);
    });
    return () => {
      cancelled = true;
    };
  }, [
    fieldConfiguration.start_date.periodMinDate,
    fieldConfiguration.start_date.periodMaxDate,
    fieldConfiguration.start_date.value,
    fieldConfiguration.end_date.value,
  ]);

  const {
    client: { activeClients, allClients },
    master: { activity },
  } = useSelector((state) => state);

  const timesheetClientList = useMemo(() => {
    const primary = activeClients?.length ? activeClients : allClients || [];
    // Timesheet client dropdown must show ALL clients (not just un-activated).
    // Backend returns Activated=1 for active clients, so filtering this hides everything.
    return primary;
  }, [activeClients, allClients]);

  const onItemAction = async (type, actionData, index) => {
    // for delete the row
    if (actionData.timesheet_item_id) {
      statusMode.current = {
        ...statusMode.current,
        currentDataItemRemoved: [
          ...statusMode.current.currentDataItemRemoved,
          actionData.timesheet_item_id,
        ],
      };
    }
    if (![undefined, null].includes(index)) {
      setSubActivityOptionsByRow((prev) => {
        const next = [...prev];
        next.splice(index, 1);
        return next;
      });
      setClientInputModeByRow((prev) => {
        const next = [...prev];
        next.splice(index, 1);
        return next;
      });
      setFieldConfiguration((prev) => {
        const nextDetails = [...(prev.itemDetails || [])];
        nextDetails.splice(index, 1);
        const total = calcTotalFromDetails(nextDetails);
        return {
          ...prev,
          itemDetails: nextDetails,
          total_hours: { ...prev.total_hours, value: total },
        };
      });
    }
  };

  const calculation = (mutated_time, rowIndex) => {
    let totalSeconds = fieldConfiguration?.itemDetails?.reduce(
      (accumulator, currentValue, index) => {
        const time = index === rowIndex ? mutated_time : currentValue.hours;
        if (!time) {
          return accumulator;
        }
        const currentDuration = time.split(":");
        const hrs = parseInt(currentDuration[0], 10);
        const min = parseInt(currentDuration[1], 10);
        const currDurationSec = 60 * min + 60 * 60 * hrs;
        return (accumulator += currDurationSec);
      },
      0,
    );
    const hours = Math.floor(totalSeconds / 3600);
    totalSeconds %= 3600;
    const minutes = Math.floor(totalSeconds / 60);
    return `${hours}:${String(minutes).padStart(2, "0")}`;
  };

  const calcTotalFromDetails = (itemDetails) => {
    let totalSeconds = (itemDetails || []).reduce((acc, item, index) => {
      const time = item.hours;
      if (!time) return acc;
      const parts = String(time).split(":");
      const hrs = parseInt(parts[0], 10) || 0;
      const min = parseInt(parts[1], 10) || 0;
      return acc + 60 * min + 3600 * hrs;
    }, 0);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    return `${hours}:${String(minutes).padStart(2, "0")}`;
  };

  const onChange = async (value, key, rowIndex, inlineCallback, action) => {
    if (key === "start_date") {
      const pickedYmd =
        formatDateOnlyYmd(value) || (typeof value === "string" ? value : "");
      const { startDate, endDate } = getTimesheetPeriodRange(pickedYmd);
      setFieldConfiguration((prev) => ({
        ...prev,
        start_date: {
          ...prev.start_date,
          value: startDate,
          periodMinDate: startDate,
          periodMaxDate: endDate,
          error: false,
          errmsg: null,
        },
        end_date: {
          ...prev.end_date,
          value: endDate,
          error: false,
          errmsg: null,
        },
        itemDetails: (prev.itemDetails || []).map((row) => ({
          ...row,
          date: "",
        })),
      }));
      setTableUpdateKey((prev) => prev + 1);
      setSubActivityOptionsByRow([]);
      setClientInputModeByRow([]);
      return;
    }

    if (key === "end_date") {
      // const formatted = moment(value).format("YYYY-MM-DD");

      // fieldConfiguration.end_date.value = formatted;
      // fieldConfiguration.start_date.maxDate = formatted; // ✅ set max
      // fieldConfiguration.end_date.error = false;
      // fieldConfiguration.end_date.errmsg = null;

      // setTableUpdateKey((prev) => prev + 1);

      // setFieldConfiguration({ ...fieldConfiguration });
      return;
    }

    // if (key === "end_date") {
    //   const errorcheck = ValidationLibrary.checkValidation(
    //     value,
    //     fieldConfiguration[key].validation
    //   );

    //   fieldConfiguration[key].value = moment(value).format("YYYY-MM-DD");
    //   fieldConfiguration[key].error = !errorcheck.state;
    //   fieldConfiguration[key].errmsg = errorcheck.msg;
    //   setTableUpdateKey((prev) => prev + 1);
    //   setFieldConfiguration({ ...fieldConfiguration });
    //   return;
    // }

    if (key === "date" && rowIndex !== undefined) {
      const normalized = formatDateOnlyYmd(value) || value;
      if (holidayDateSet.has(String(normalized || "").slice(0, 10))) {
        Toaster.error("Timesheet entries cannot be added on a holiday");
        return;
      }
      setFieldConfiguration((prev) => {
        const nextDetails = [...(prev.itemDetails || [])];
        if (nextDetails[rowIndex]) {
          nextDetails[rowIndex] = {
            ...nextDetails[rowIndex],
            date: normalized,
          };
        }
        return { ...prev, itemDetails: nextDetails };
      });
      return;
    }

    if (rowIndex !== undefined && rowIndex !== null) {
      const fieldValue = key === "hours" ? normalizeTimeHHmm(value) : value;
      setFieldConfiguration((prev) => {
        const nextDetails = [...(prev.itemDetails || [])];
        if (!nextDetails[rowIndex]) return prev;
        const isClientTextMode = !!clientInputModeByRow[rowIndex];
        const nextRow = { ...nextDetails[rowIndex], [key]: fieldValue };
        if (key === "client_id") {
          const selectedClientOption = (clientsData || []).find(
            (option) => String(option.id) === String(value),
          );
          const isOthersSelection =
            value === "Others" || selectedClientOption?.value === "Others";

          if (isOthersSelection) {
            nextRow.client_id = "";
            nextRow.business_name = "";
            setClientInputModeByRow((prev) => {
              const next = [...prev];
              next[rowIndex] = true;
              return next;
            });
          } else if (isClientTextMode) {
            nextRow.client_id = value;
          } else {
            nextRow.business_name = !value
              ? ""
              : (() => {
                  const clientData = (timesheetClientList || []).find(
                    (data) => data.client_id === value,
                  );
                  return (
                    clientData?.business_name ?? nextRow.business_name ?? ""
                  );
                })();
          }
        }
        if (key === "activity_id") {
          nextRow.sub_activity_id = "";
          if (value !== "Others") nextRow.custom_sub_activity = "";
        }
        if (key === "billed_type") {
          nextRow.activity_id = "";
          nextRow.sub_activity_id = "";
          nextRow.custom_sub_activity = "";
          const billedNorm = normalizeBilledType(value);
          if (billedNorm === "nonbillable" || billedNorm === "leave") {
            nextRow.client_id = "";
            nextRow.business_name = "";
            setClientInputModeByRow((prev) => {
              const next = [...prev];
              next[rowIndex] = false;
              return next;
            });
          }
          // Leave hours are limited to 4 or 8 — clear invalid existing values
          if (billedNorm === "leave") {
            const hours = String(nextRow.hours || "").trim();
            if (
              hours !== "04:00" &&
              hours !== "08:00" &&
              hours !== "4:00" &&
              hours !== "8:00"
            ) {
              nextRow.hours = "";
            } else if (hours === "4:00") {
              nextRow.hours = "04:00";
            } else if (hours === "8:00") {
              nextRow.hours = "08:00";
            }
          }
          setSubActivityOptionsByRow((prevOpts) => {
            const nextOpts = [...prevOpts];
            nextOpts[rowIndex] = [];
            return nextOpts;
          });
        }
        nextDetails[rowIndex] = nextRow;
        const next = { ...prev, itemDetails: nextDetails };
        if (key === "hours" || key === "billed_type") {
          next.total_hours = {
            ...prev.total_hours,
            value: calcTotalFromDetails(nextDetails),
          };
        }
        return next;
      });
    }
  };

  const clientsData = useMemo(() => {
    const list = (timesheetClientList || []).map((data) => {
      const { client_id, email } = data;
      return {
        id: client_id,
        value: dropdownName(data) + " - " + email,
      };
    });
    return [
      { id: "", value: "Clear selection", keepAtTop: true },
      { id: "Others", value: "Others", keepAtTop: true },
      ...list,
    ];
  }, [timesheetClientList]);

  const activityOptions = useMemo(() => {
    if (!activity || !Array.isArray(activity)) return [];
    return activity
      .map(normalizeActivityOption)
      .filter((a) => !isOthersActivityOption(a));
  }, [activity]);

  const filterActivitiesByBilledType = React.useCallback(
    (billedType) => {
      if (!Array.isArray(activityOptions)) return [];
      const target = normalizeBilledType(billedType);
      if (!target) return activityOptions;
      if (target === "leave") return [];
      // If none of the activity master entries carry a billing_type field
      // (e.g. backend doesn't return that column yet), fall back to showing
      // all options so the dropdown isn't empty.
      const anyHasBillingType = activityOptions.some((a) =>
        normalizeBilledType(
          a?.billing_type || a?.billed_type || a?.billingType,
        ),
      );
      if (!anyHasBillingType) return activityOptions;
      const filtered = activityOptions.filter((a) => {
        const bt = normalizeBilledType(
          a?.billing_type || a?.billed_type || a?.billingType,
        );
        return bt === target;
      });
      return filtered;
    },
    [activityOptions],
  );

  const activityDependentDropdown = useMemo(() => {
    return (fieldConfiguration.itemDetails || []).map((row) =>
      filterActivitiesByBilledType(row?.billed_type),
    );
  }, [fieldConfiguration.itemDetails, filterActivitiesByBilledType]);

  const isNonBillableRow = React.useCallback(
    (row) => normalizeBilledType(row?.billed_type) === "nonbillable",
    [],
  );

  const isLeaveRow = React.useCallback(
    (row) => isLeaveBilledType(row?.billed_type),
    [],
  );

  const isClientBusinessDisabledRow = React.useCallback(
    (row) => isNonBillableRow(row) || isLeaveRow(row),
    [isNonBillableRow, isLeaveRow],
  );

  const defaultFields = useMemo(() => {
    return [
      {
        type: "datepicker",
        validation: [{ name: "required" }],
        error: null,
        errmsg: null,
        changeData: onChange,
        disabled: viewModeRef.current || !isDateRangeSelected,
        minDate: fieldConfiguration.start_date.periodMinDate || null,
        maxDate: fieldConfiguration.start_date.periodMaxDate || null,
        shouldDisableDate: (date) =>
          holidayDateSet.has(
            formatDateOnlyYmd(date) || moment(date).format("YYYY-MM-DD"),
          ),
      },
      {
        type: "select",
        validation: [{ name: "required" }],
        dropdown: billedTypeOptions,
        error: null,
        errmsg: null,
        changeData: onChange,
        disabled: viewModeRef.current || false,
      },
      {
        type: "select",
        // validation: [{ name: "required" }],
        dropdown: clientsData,
        error: null,
        errmsg: null,
        getFieldType: (rowIndex, rows) => {
          return clientInputModeByRow[rowIndex] ? "text" : "select";
        },
        getValidation: (rowIndex, rows) => {
          return clientInputModeByRow[rowIndex] ? [{ name: "emailNew" }] : [];
        },
        getDisabled: (rowIndex, rows) => {
          const item = rows[rowIndex];
          return viewModeRef.current || isClientBusinessDisabledRow(item);
        },
        changeData: onChange,
        disabled: viewModeRef.current || false,
      },
      {
        type: "text",
        value: "",
        error: null,
        errmsg: null,
        getDisabled: (rowIndex, rows) => {
          const item = rows[rowIndex];
          return viewModeRef.current || isClientBusinessDisabledRow(item);
        },
        changeData: onChange,
        disabled: viewModeRef.current || false,
      },
      {
        type: "select",
        validation: [{ name: "required" }],
        error: null,
        errmsg: null,
        dropdown: activityOptions,
        dependentDropdown: activityDependentDropdown,
        opt_id: "activity_id",
        opt_value: "activity",
        getValidation: (rowIndex, rows) => {
          if (isLeaveRow(rows[rowIndex])) return [];
          return [{ name: "required" }];
        },
        getDisabled: (rowIndex, rows) => {
          return viewModeRef.current || isLeaveRow(rows[rowIndex]);
        },
        changeData: async (value, key, rowIndex) => {
          const activityId =
            typeof value === "number" && value > 0
              ? value
              : (() => {
                  const n = Number(value);
                  if (!Number.isNaN(n) && n > 0) return n;
                  const act = (activityOptions || []).find(
                    (a) =>
                      String(a.value || a.activity || "").trim() ===
                        String(value).trim() ||
                      String(a.id) === String(value) ||
                      String(a.activity_id) === String(value),
                  );
                  return act ? (act.id ?? act.activity_id) : value;
                })();
          const idForApi = Number(activityId);
          const hasValidId = !Number.isNaN(idForApi) && idForApi > 0;
          if (!hasValidId) {
            setSubActivityOptionsByRow((prev) => {
              const next = [...prev];
              next[rowIndex] = [];
              return next;
            });
          } else {
            // Cache sub-activities per activity_id to avoid repeated calls.
            subActivityCacheRef.current = subActivityCacheRef.current || {};
            const cached = subActivityCacheRef.current[idForApi];
            const sub_activities =
              cached || (await MASTER_ACTION.getSubActivity(idForApi));
            if (sub_activities instanceof Array) {
              if (!cached)
                subActivityCacheRef.current[idForApi] = sub_activities;
              setSubActivityOptionsByRow((prev) => {
                const next = [...prev];
                next[rowIndex] = sub_activities;
                return next;
              });
            } else {
              Toaster.error(sub_activities || "Failed to load sub-activities");
            }
          }
          onChange(value, key, rowIndex);
        },
        disabled: viewModeRef.current || false,
      },
      {
        type: "select", // Default type, will be overridden dynamically
        validation: [{ name: "required" }],
        error: null,
        errmsg: null,
        changeData: onChange,
        dependentDropdown: (fieldConfiguration.itemDetails || []).map(
          (_, i) => subActivityOptionsByRow[i] ?? [],
        ),
        disabled: viewModeRef.current || false,
        opt_id: "sub_activity_id",
        opt_value: "sub_activity",
        // Explicitly set the key for this field
        fieldKey: "sub_activity_id",
        // Custom function to conditionally change type based on row data
        getFieldType: (rowIndex, rows) => {
          const item = rows[rowIndex];
          if (!item) return "select";
          if (isLeaveRow(item)) return "select";
          if (normalizeBilledType(item.billed_type) === "nonbillable")
            return "text";
          if (item.activity_id === "Others") return "text";
          return "select";
        },
        // Sub-activity required except Leave (and keep Non-billable as free text when applicable)
        getValidation: (rowIndex, rows) => {
          if (isLeaveRow(rows[rowIndex])) return [];
          return [{ name: "required" }];
        },
        getDisabled: (rowIndex, rows) => {
          return viewModeRef.current || isLeaveRow(rows[rowIndex]);
        },
      },
      {
        type: "textarea",
        // validation: [{ name: "required" }],
        value: "",
        error: null,
        errmsg: null,
        changeData: onChange,
        disabled: viewModeRef.current || false,
        rows: 3,
      },
      {
        type: "timeselector",
        validation: [{ name: "required" }],
        error: null,
        errmsg: null,
        // Leave billing: only 4 or 8 hours selectable
        getFieldType: (rowIndex, rows) =>
          isLeaveRow(rows[rowIndex]) ? "select" : "timeselector",
        getDropdown: (rowIndex, rows) =>
          isLeaveRow(rows[rowIndex])
            ? [
                { id: "04:00", value: "04:00" },
                { id: "08:00", value: "08:00" },
              ]
            : [],
        changeData: (value, key, rowIndex) => {
          const inlineCallback = (error) => {
            defaultFields[1].validation[1].customValidate = error;
          };
          onChange(value, key, rowIndex, inlineCallback);
        },
        disabled: viewModeRef.current || false,
      },
      {
        type: "label",
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    clientsData,
    viewModeRef.current,
    activity,
    activityOptions,
    activityDependentDropdown,
    subActivityOptionsByRow,
    clientInputModeByRow,
    fieldConfiguration.start_date.periodMinDate,
    fieldConfiguration.start_date.periodMaxDate,
    holidayDateSet,
    fieldConfiguration.itemDetails,
    isLeaveRow,
    isClientBusinessDisabledRow,
  ]);

  async function onSubmit(statusValue) {
    try {
      const mainvalue = {};
      const targetkeys = Object.keys(fieldConfiguration);
      for (const i in targetkeys) {
        if (targetkeys[i] === "itemDetails") {
          continue;
        }
        const errorcheck = ValidationLibrary.checkValidation(
          fieldConfiguration[targetkeys[i]].value,
          fieldConfiguration[targetkeys[i]].validation,
        );
        fieldConfiguration[targetkeys[i]].error = !errorcheck.state;
        fieldConfiguration[targetkeys[i]].errmsg = errorcheck.msg;
        mainvalue[targetkeys[i]] = fieldConfiguration[targetkeys[i]].value;
      }

      const billableRowError = (fieldConfiguration.itemDetails || []).some(
        (item) =>
          item?.billed_type === "Billable" &&
          (!(item?.client_id || "").trim() ||
            !(item?.business_name || "").trim()),
      );
      if (billableRowError) {
        Toaster.error(
          "Client and Business Name are required for Billable items",
        );
        return;
      }

      if (
        Number(statusValue) !== 0 &&
        isTimesheetTotalBelowMinimum(
          fieldConfiguration.total_hours?.value,
          requiredTimesheetHours,
        )
      ) {
        Toaster.error(
          `Total hours must be at least ${requiredTimesheetHours} hours to submit the timesheet`,
        );
        return;
      }

      const filtererr = targetkeys.filter(
        (obj) => fieldConfiguration[obj].error === true,
      );
      if (filtererr.length > 0) {
        Toaster.error("Please fill up required fields");
      } else {
        const checkValidity = submitValidateRef.current;
        const filterError = checkValidity();
        if (filterError) {
          Toaster.error("Please fill up required fields");
        } else {
          let itemParams = [];
          if (statusMode.current?.currentDataItemRemoved?.length > 0) {
            const selectedData = statusMode.current?.currentData;
            itemParams = statusMode.current.currentDataItemRemoved.map(
              (_id) => {
                let {
                  date,
                  client_id,
                  business_name,
                  activity_id,
                  sub_activity_id,
                  hours,
                  description,
                  custom_sub_activity,
                } = selectedData.itemDetails.find(
                  (data) => data.timesheet_item_id === _id,
                );
                return {
                  date,
                  client_id,
                  business_name,
                  activity_id,
                  sub_activity_id,
                  hours,
                  description,
                  custom_sub_activity: custom_sub_activity || "",
                  timesheet_item_id: _id,
                  status: 0,
                };
              },
            );
            itemParams = [...fieldConfiguration.itemDetails, ...itemParams];
          } else {
            itemParams = fieldConfiguration.itemDetails;
          }

          // Process itemParams to handle "Others" case properly
          itemParams = itemParams.map((item, rowIndex) => {
            const processedItem = { ...item, client_email: "" };

            // Format date if it exists and is not already formatted
            if (processedItem.date && typeof processedItem.date !== "string") {
              processedItem.date = moment(processedItem.date).format(
                "YYYY-MM-DD",
              );
            } else if (
              processedItem.date &&
              !processedItem.date.match(/^\d{4}-\d{2}-\d{2}$/)
            ) {
              processedItem.date = moment(processedItem.date).format(
                "YYYY-MM-DD",
              );
            }

            // Handle "Others" case - database expects integer for activity_id and sub_activity_id
            if (processedItem.activity_id === "Others") {
              // Save the custom text before converting sub_activity_id
              const customText =
                processedItem.sub_activity_id &&
                typeof processedItem.sub_activity_id === "string"
                  ? processedItem.sub_activity_id
                  : "";

              // Set activity_id to NULL (foreign key constraint requires valid activity_id or NULL)
              processedItem.activity_id = null;
              // Set sub_activity_id to NULL (since it must be an integer, not text)
              processedItem.sub_activity_id = null;
              // Store the custom text in a new field: custom_sub_activity
              // This field needs to be added to the database table f_tbl_pm_timesheet_items
              processedItem.custom_sub_activity = customText || "";
            } else {
              // For normal activities, ensure activity_id is a number (not string)
              if (
                typeof processedItem.activity_id === "string" &&
                processedItem.activity_id !== ""
              ) {
                processedItem.activity_id =
                  parseInt(processedItem.activity_id, 10) || null;
              }
              // Ensure sub_activity_id is a number for normal activities
              if (
                typeof processedItem.sub_activity_id === "string" &&
                processedItem.sub_activity_id !== "" &&
                !isNaN(processedItem.sub_activity_id)
              ) {
                processedItem.sub_activity_id = parseInt(
                  processedItem.sub_activity_id,
                  10,
                );
              }
            }

            // Non-billable rows: sub-activity is free text, store it in
            // custom_sub_activity and null out sub_activity_id (FK is integer).
            if (
              normalizeBilledType(processedItem.billed_type) === "nonbillable"
            ) {
              const subVal = processedItem.sub_activity_id;
              if (typeof subVal === "string" && subVal.trim() !== "") {
                processedItem.custom_sub_activity = subVal.trim();
                processedItem.sub_activity_id = null;
              } else if (subVal === "" || subVal === undefined) {
                processedItem.sub_activity_id = null;
              }
            }

            // Leave rows: activity / sub-activity are not required
            if (isLeaveBilledType(processedItem.billed_type)) {
              processedItem.activity_id = null;
              processedItem.sub_activity_id = null;
              processedItem.custom_sub_activity = "";
              processedItem.client_id = null;
              processedItem.business_name = "";
              processedItem.client_email = "";
            }

            // Client "Others" free-text mode: treat entered text as email and send separately
            if (clientInputModeByRow?.[rowIndex]) {
              processedItem.client_email = String(
                processedItem.client_id || "",
              ).trim();
              processedItem.client_id = null;
            }

            return processedItem;
          });

          const updateMode = statusMode.current?.timesheet_id ? true : false;
          const payload = {};
          const index = targetkeys.indexOf("itemDetails");
          if (index !== -1) {
            targetkeys.splice(index, 1);
          }
          targetkeys.forEach(
            (data) => (payload[data] = fieldConfiguration[data].value),
          );
          let payloadData = {
            ...payload,
            status: statusValue,
            total_hours: fieldConfiguration.total_hours.value,
            employee_id: localStorage.getItem("user_id"),
            itemDetails: itemParams,
          };

          const response = await TIMESHEET_ACTION.insertUpdateTimesheet({
            params: payloadData,
            insertUpdateFlag: updateMode ? 1 : 0,
            timesheet_id: statusMode.current?.timesheet_id || null,
          });

          if (
            response &&
            response.status === 200 &&
            response.data &&
            response.data.status === 1
          ) {
            Toaster.success(
              `Timesheet ${
                updateMode
                  ? statusValue === 0
                    ? "saved"
                    : "submitted"
                  : "created"
              } successfully`,
            );
            statusValue && handleCancel();
          } else if (
            response &&
            response.status === 200 &&
            response.data &&
            response.data.status === 0
          ) {
            Toaster.warning(response.data.message || "Operation failed");
          } else {
            Toaster.error(
              `Error occurred while ${
                updateMode
                  ? statusValue === 0
                    ? "saving"
                    : "submitting"
                  : "creating"
              } the Timesheet`,
            );
          }
        }
      }
      setFieldConfiguration((prevState) => ({
        ...prevState,
      }));
    } catch (err) {
      console.error("Error in onSubmit:", err);
      Toaster.error(
        err.message || "An error occurred while submitting the timesheet",
      );
    }
  }

  function handleCancel() {
    viewModeRef.current = false;
    setSubActivityOptionsByRow([]);
    setClientInputModeByRow([]);
    statusMode.current = {};
    const cancel = cancelValidateRef.current;
    cancel();
    setFieldConfiguration(defaultValues);
  }

  const handleDownloadSampleExcel = async () => {
    const weekStart =
      fieldConfiguration.start_date.periodMinDate ||
      fieldConfiguration.start_date.value ||
      "";
    const weekEnd =
      fieldConfiguration.start_date.periodMaxDate ||
      fieldConfiguration.end_date.value ||
      weekStart;
    const wb = await buildTimesheetSampleWorkbook({
      activityList: activity || [],
      startDate: weekStart,
      endDate: weekEnd,
    });
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Timesheet_Sample_${weekStart || "template"}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
    Toaster.success("Sample Excel downloaded");
  };

  const getSheetCellValue = (sheet, rowIndex, colIdx) => {
    if (colIdx < 0) return "";
    const ref = utils.encode_cell({ r: rowIndex, c: colIdx });
    const cell = sheet[ref];
    if (!cell) return "";
    if (cell.w != null && String(cell.w).trim() !== "") return cell.w;
    if (cell.v instanceof Date) return cell.v;
    return cell.v ?? "";
  };

  const getHoursCellValue = (sheet, rowIndex, colIdx) => {
    if (colIdx < 0) return "";
    const ref = utils.encode_cell({ r: rowIndex, c: colIdx });
    const cell = sheet[ref];
    if (!cell) return "";
    // Prefer formatted string when it represents an HH:MM-like value
    // (so 0:50 doesn't become 0.5 or 12:00 depending on raw numeric).
    const w = cell.w != null ? String(cell.w).trim() : "";
    if (w && /^\d{1,2}[:.]\d{1,2}(?::\d{1,2})?$/.test(w)) return w;
    if (cell.v instanceof Date) return cell.v;
    if (cell.t === "n" && typeof cell.v === "number") return cell.v;
    if (w) return w;
    return cell.v ?? "";
  };

  const handleUploadExcel = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    e.target.value = "";
    const minDate = fieldConfiguration.start_date.periodMinDate;
    const maxDate = fieldConfiguration.start_date.periodMaxDate;
    try {
      const data = await file.arrayBuffer();
      const wb = read(data, { type: "array", cellDates: true });
      const sheetName =
        wb.SheetNames.find((n) => /timesheet/i.test(String(n))) ||
        wb.SheetNames.find((n) => !/lists/i.test(String(n))) ||
        wb.SheetNames[0];
      const firstSheet = wb.Sheets[sheetName];
      const rawRows = utils.sheet_to_json(firstSheet, {
        header: 1,
        defval: "",
      });
      if (rawRows.length < 2) {
        Toaster.error("Excel must have a header row and at least one data row");
        return;
      }
      const headerRow = rawRows[0].map((h) => String(h || "").trim());
      const colIndex = (name) => {
        const i = headerRow.findIndex(
          (h) =>
            h.toLowerCase().replace(/\s+/g, " ") ===
            name.toLowerCase().replace(/\s+/g, " "),
        );
        return i >= 0 ? i : -1;
      };
      // Robust column resolver:
      // - Prefer header matching (works for most files)
      // - Fallback to the official template layout (A..H) if headers are missing/changed
      const TEMPLATE_COLS = {
        Date: 0,
        "Billed Type": 1,
        Client: 2,
        "Business Name": 3,
        Activity: 4,
        "Sub Activity": 5,
        Description: 6,
        Hours: 7,
      };
      const resolveCol = (name) => {
        const byHeader = colIndex(name);
        if (byHeader >= 0) return byHeader;
        return TEMPLATE_COLS[name] ?? -1;
      };
      const get = (row, name) => {
        const i = resolveCol(name);
        return i >= 0 ? row[i] : "";
      };
      const hoursColIdx = resolveCol("Hours");
      const dateColIdx = resolveCol("Date");
      const activityColIdx = resolveCol("Activity");
      const subActivityColIdx = resolveCol("Sub Activity");
      const rows = rawRows
        .slice(1)
        .filter((row) =>
          row.some((cell) => cell != null && String(cell).trim() !== ""),
        );
      if (rows.length === 0) {
        Toaster.error("No data rows found in the Excel file");
        return;
      }
      const activityList = (activity || []).map(normalizeActivityOption);
      const activityLabelToId = {};
      const activityLabelToIdForBilledType = (billedType) => {
        const target = normalizeBilledType(billedType);
        const map = {};
        activityList.forEach((a) => {
          const id = getActivityCanonicalId(a);
          const label = String(a.value || a.activity || "").trim();
          if (!id || !label) return;
          if (isOthersActivityOption(a)) return;
          const bt = normalizeBilledType(
            a.billing_type || a.billed_type || a.billingType,
          );
          if (bt === target) {
            map[normalizeLabel(label)] = id;
            map[normalizeLabelForMatch(label)] = id;
          }
        });
        return map;
      };
      activityList.forEach((a) => {
        const id = getActivityCanonicalId(a);
        const label = String(a.value || a.activity || "").trim();
        if (id && label) {
          activityLabelToId[normalizeLabel(label)] = id;
          activityLabelToId[normalizeLabelForMatch(label)] = id;
        }
      });

      // Fetch sub-activities once (instead of per-activity) to avoid API spam.
      const subActivityToActivity = {};
      const subActivityByActivity = {};
      const allSubs = await MASTER_ACTION.getSubActivity();
      if (Array.isArray(allSubs)) {
        allSubs.map(normalizeSubActivityOption).forEach((s) => {
          const actId = s.activity_id ?? s.activityId ?? s.activityID;
          if (!actId) return;
          const key = String(actId);
          if (!subActivityByActivity[key]) subActivityByActivity[key] = [];
          subActivityByActivity[key].push(s);
          const label = String(s.sub_activity || s.value || "").trim();
          if (label) {
            const subId = s.sub_activity_id ?? s.id ?? "";
            const entry = {
              activity_id: actId,
              sub_activity_id: subId,
              sub_activity_label: label,
            };
            subActivityToActivity[normalizeLabel(label)] = entry;
            subActivityToActivity[normalizeLabelForMatch(label)] = entry;
          }
        });
      }
      const clientByBusinessName = {};
      const clientByDisplay = {};
      const clientByDisplayLower = {};
      const clientByEmail = {};
      (timesheetClientList || []).forEach((c) => {
        const bn = (c.business_name || "").trim();
        if (bn) clientByBusinessName[bn] = c.client_id;
        const disp = `${dropdownName(c)} - ${c.email || ""}`.trim();
        if (disp) {
          clientByDisplay[disp] = c.client_id;
          clientByDisplayLower[disp.toLowerCase().trim()] = c.client_id;
        }
        const email = (c.email || "").trim().toLowerCase();
        if (email) clientByEmail[email] = c.client_id;
      });
      const itemDetails = [];
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        // Prefer values from sheet_to_json (most reliable across Excel/Sheets),
        // and only fall back to raw sheet cells if needed.
        const sheetRowIndex = i + 1;
        const dateCell =
          getSheetCellValue(firstSheet, sheetRowIndex, dateColIdx) ||
          get(row, "Date");
        const dateStr = parseDateFromExcel(dateCell);
        const billed_type = parseBilledTypeFromExcel(
          String(get(row, "Billed Type") || "").trim(),
        );
        const businessNameFromExcel = String(
          getSheetCellValue(
            firstSheet,
            sheetRowIndex,
            resolveCol("Business Name"),
          ) || get(row, "Business Name"),
        ).trim();
        const activityCol = String(
          getSheetCellValue(firstSheet, sheetRowIndex, activityColIdx) ||
            get(row, "Activity") ||
            "",
        ).trim();
        const clientCol = String(
          getSheetCellValue(firstSheet, sheetRowIndex, resolveCol("Client")) ||
            get(row, "Client"),
        ).trim();
        const emailFromClientCol = clientCol.includes("@")
          ? (clientCol.split(/\s*-\s*/).pop() || "").trim().toLowerCase()
          : "";
        let client_id =
          clientByDisplay[clientCol] ||
          clientByDisplayLower[clientCol.toLowerCase()] ||
          (emailFromClientCol ? clientByEmail[emailFromClientCol] : "") ||
          clientByBusinessName[businessNameFromExcel] ||
          "";
        if (client_id === "" && businessNameFromExcel) {
          const match = (timesheetClientList || []).find(
            (c) =>
              (c.business_name || "").toLowerCase() ===
              businessNameFromExcel.toLowerCase(),
          );
          if (match) client_id = match.client_id;
        }
        if (client_id === "" && clientCol) {
          const match = (timesheetClientList || []).find(
            (c) =>
              `${dropdownName(c)} - ${c.email || ""}`.trim().toLowerCase() ===
              clientCol.toLowerCase(),
          );
          if (match) client_id = match.client_id;
        }
        // If client email is provided but not found in portal, keep the email as-is
        // so UI shows it (text mode) and submit will send it as client_email.
        if (!client_id && emailFromClientCol) {
          client_id = (clientCol.split(/\s*-\s*/).pop() || clientCol).trim();
        }
        const subActivityLabel = String(
          getSheetCellValue(firstSheet, sheetRowIndex, subActivityColIdx) ||
            get(row, "Sub Activity") ||
            "",
        ).trim();
        const isLeave = isLeaveBilledType(billed_type);
        const isNonBillable =
          billed_type === "Non-billable" ||
          normalizeBilledType(billed_type) === "nonbillable";
        const clearsClientFields = isNonBillable || isLeave;
        let activity_id = "";
        let sub_activity_id = "";
        let custom_sub_activity = "";

        if (!isLeave) {
          const billedTypeActivityMap =
            activityLabelToIdForBilledType(billed_type);
          const activitiesForBilledType = activityList.filter((a) => {
            if (isOthersActivityOption(a)) return false;
            const bt = normalizeBilledType(
              a.billing_type || a.billed_type || a.billingType,
            );
            return bt === normalizeBilledType(billed_type);
          });
          const resolveActivityFromExcelLabel = (label) => {
            if (!label || normalizeLabel(label) === "others") return "";
            return (
              billedTypeActivityMap[normalizeLabelForMatch(label)] ||
              billedTypeActivityMap[normalizeLabel(label)] ||
              resolveActivityIdFromLabel(label, activitiesForBilledType) ||
              activityLabelToId[normalizeLabelForMatch(label)] ||
              activityLabelToId[normalizeLabel(label)] ||
              resolveActivityIdFromLabel(label, activityList) ||
              ""
            );
          };

          const resolvedActivityId = resolveActivityFromExcelLabel(activityCol);
          if (resolvedActivityId && resolvedActivityId !== "Others") {
            activity_id = String(resolvedActivityId);
          }

          if (subActivityLabel) {
            if (!activity_id) {
              const lookup =
                subActivityToActivity[normalizeLabelForMatch(subActivityLabel)] ||
                subActivityToActivity[normalizeLabel(subActivityLabel)];
              if (lookup) {
                activity_id = String(lookup.activity_id);
                if (isNonBillable) {
                  sub_activity_id = lookup.sub_activity_label || subActivityLabel;
                  custom_sub_activity = sub_activity_id;
                } else {
                  sub_activity_id = String(lookup.sub_activity_id ?? "");
                }
              }
            }

            if (activity_id && activity_id !== "Others") {
              const subs =
                subActivityByActivity[activity_id] ||
                subActivityByActivity[String(activity_id)] ||
                [];
              const match = findSubActivityMatch(subs, subActivityLabel);
              if (match) {
                if (isNonBillable) {
                  sub_activity_id = String(
                    match.sub_activity || match.value || subActivityLabel,
                  ).trim();
                  custom_sub_activity = sub_activity_id;
                } else {
                  sub_activity_id = String(
                    match.sub_activity_id ?? match.id ?? "",
                  );
                }
              } else if (!sub_activity_id && isNonBillable) {
                sub_activity_id = subActivityLabel;
                custom_sub_activity = subActivityLabel;
              }
            } else if (!activity_id && isNonBillable) {
              sub_activity_id = subActivityLabel;
              custom_sub_activity = subActivityLabel;
              if (resolvedActivityId) {
                activity_id = String(resolvedActivityId);
              }
            }
          } else if (resolvedActivityId) {
            activity_id = String(resolvedActivityId);
          } else if (activityCol && !isNonBillable) {
            const fromActivityOnly = resolveActivityFromExcelLabel(activityCol);
            if (fromActivityOnly) activity_id = String(fromActivityOnly);
          }

          if (activity_id && activity_id !== "Others") {
            const rowActivities = filterActivitiesByBilledType(billed_type);
            activity_id = coerceSelectOptionId(activity_id, rowActivities, [
              "id",
              "activity_id",
            ]);
            if (
              sub_activity_id &&
              !isNonBillable &&
              typeof sub_activity_id === "string" &&
              !sub_activity_id.includes("@")
            ) {
              const subs =
                subActivityByActivity[activity_id] ||
                subActivityByActivity[String(activity_id)] ||
                [];
              sub_activity_id = coerceSelectOptionId(sub_activity_id, subs, [
                "id",
                "sub_activity_id",
              ]);
            }
          }
        }
        const description = String(get(row, "Description")).trim();
        const hoursCell = getHoursCellValue(
          firstSheet,
          sheetRowIndex,
          hoursColIdx,
        );
        let hours = parseHoursFromExcel(
          hoursCell !== "" && hoursCell != null ? hoursCell : get(row, "Hours"),
        );
        // Minimum required fields for an importable row.
        // The downloaded template always provides Date; Hours may be entered as
        // 0:50, 0.50 (meaning 0:50), or a time cell.
        if (!dateStr) {
          // If the row has no Date, it can't be imported.
          continue;
        }
        if (!hours) {
          // Hours is required for a meaningful timesheet line.
          continue;
        }
        if (isLeave) {
          const leaveHours =
            normalizeLeaveHoursForImport(hours) ||
            normalizeLeaveHoursForImport(hoursCell) ||
            normalizeLeaveHoursForImport(get(row, "Hours"));
          if (!leaveHours) {
            Toaster.warning(
              `Row ${i + 2}: Leave hours must be 04:00 or 08:00 — skipped.`,
            );
            continue;
          }
          hours = leaveHours;
        }
        let resolvedDate = formatDateOnlyYmd(dateStr);
        if (!resolvedDate && minDate) resolvedDate = formatDateOnlyYmd(minDate);
        if (minDate && maxDate && resolvedDate) {
          if (compareDateOnly(resolvedDate, minDate) < 0) {
            Toaster.warning(
              `Row ${i + 2}: date ${resolvedDate} is before the selected period (${minDate}–${maxDate}).`,
            );
          } else if (compareDateOnly(resolvedDate, maxDate) > 0) {
            Toaster.warning(
              `Row ${i + 2}: date ${resolvedDate} is after the selected period (${minDate}–${maxDate}).`,
            );
          }
        }
        if (!resolvedDate) {
          Toaster.warning(`Row ${i + 2}: could not parse Date — skipped.`);
          continue;
        }
        if (holidayDateSet.has(resolvedDate)) {
          Toaster.warning(
            `Row ${i + 2}: ${resolvedDate} is a holiday and was skipped.`,
          );
          continue;
        }
        const clientData = (timesheetClientList || []).find(
          (c) => c.client_id === client_id,
        );
        const business_name =
          businessNameFromExcel ||
          (clientData ? clientData.business_name || "" : "") ||
          "";
        itemDetails.push({
          date: resolvedDate,
          billed_type,
          client_id: clearsClientFields ? "" : client_id || "",
          business_name: clearsClientFields ? "" : business_name,
          activity_id: isLeave
            ? ""
            : activity_id === "Others"
              ? "Others"
              : activity_id
                ? String(activity_id)
                : "",
          sub_activity_id: isLeave ? "" : (sub_activity_id ?? ""),
          description,
          hours: hours || "00:00",
          custom_sub_activity: isLeave ? "" : custom_sub_activity || "",
        });
      }
      if (itemDetails.length === 0) {
        Toaster.error(
          "No valid rows to import. Check Date, Client, Sub Activity and Hours.",
        );
        return;
      }
      itemDetails.sort((a, b) => compareDateOnly(a.date, b.date));
      setClientInputModeByRow(
        itemDetails.map(
          (item) =>
            typeof item?.client_id === "string" && item.client_id.includes("@"),
        ),
      );
      setSubActivityOptionsByRow(
        itemDetails.map((item) => {
          if (!item.activity_id || item.activity_id === "Others") return [];
          return (
            subActivityByActivity[item.activity_id] ||
            subActivityByActivity[String(item.activity_id)] ||
            []
          );
        }),
      );
      const newConfig = { ...fieldConfiguration };
      newConfig.itemDetails = itemDetails;
      let totalSeconds = 0;
      itemDetails.forEach((item) => {
        const time = item.hours || "0:0";
        const parts = time.toString().split(":");
        const hrs = parseInt(parts[0], 10) || 0;
        const min = parseInt(parts[1], 10) || 0;
        totalSeconds += 60 * min + 3600 * hrs;
      });
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      newConfig.total_hours.value = `${hours}:${String(minutes).padStart(2, "0")}`;
      setFieldConfiguration(newConfig);
      setTableUpdateKey((k) => k + 1);
      Toaster.success(`Imported ${itemDetails.length} item(s) from Excel`);
    } catch (err) {
      console.error(err);
      Toaster.error(err?.message || "Failed to parse Excel file");
    }
  };

  useLayoutEffect(() => {
    statusMode.current = {
      status: 0,
      timesheet_id: null,
      currentData: null,
      currentDataItemRemoved: [],
    };

    dispatch(EMPLOYEE_ACTION.getActiveEmployees());
    dispatch(CLIENT_ACTION.getActiveClients({ scope: "all" }));

    if (location.state) {
      onChange("2023-03-13", "start_date", false, false, location.state);
    }
  }, []);

  useEffect(() => {
    const role = (localStorage.getItem("user_role") || "").toLowerCase();
    // Admin can create timesheets too; ensure client dropdown is populated on first load.
    if (role !== "employee" && role !== "manager" && role !== "admin") return;
    if (activeClients?.length > 0) return;
    dispatch(CLIENT_ACTION.getAllClients({ scope: "all" }));
  }, [dispatch, activeClients?.length]);

  const timesheetStartPickerMinDate = useMemo(
    () => getTimesheetPeriodPickerMinDate(12),
    [],
  );

  const timesheetStartPickerMaxDate = useMemo(
    () => getEndOfCurrentWeekYmd(),
    [],
  );

  return (
    <div
      className="c"
      style={{ backgroundColor: "#f5f7fa", padding: 24, height: "100%" }}
    >
      {/* Header Section */}
      <div className="timesheet-page-header">
        <h1 className="timesheet-title"></h1>
        <Button
          // iconCls="btn_icon"
          icon={<AddCircleOutlineOutlined />}
          text="Add New"
          handleClick={handleCancel}
          className="timesheet-add-new-btn"
          variant="outline"
        />
      </div>

      {/* Date and Total Hours Card */}
      <div className="timesheet-summary-card panel">
        <div className="timesheet-summary-content">
          <div className="timesheet-date-field flex " style={{ gap: 16 }}>
            <Labelbox
              type="datepicker"
              labelname="Start Date"
              changeData={(e) => onChange(e, "start_date")}
              value={fieldConfiguration.start_date.value}
              error={fieldConfiguration.start_date.error}
              errmsg={fieldConfiguration.start_date.errmsg}
              minDate={timesheetStartPickerMinDate}
              maxDate={timesheetStartPickerMaxDate}
            />

            <Labelbox
              type="datepicker"
              labelname="End Date"
              changeData={(e) => onChange(e, "end_date")}
              value={fieldConfiguration.end_date.value}
              error={fieldConfiguration.end_date.error}
              errmsg={fieldConfiguration.end_date.errmsg}
              disabled
            />
          </div>
          <div className="timesheet-total-hours">
            <span className="timesheet-total-hours-label">Total Hours:</span>
            <span className="timesheet-total-hours-value">
              {fieldConfiguration.total_hours.value || "00:00"}
            </span>
          </div>
        </div>
        {fieldConfiguration.rejected_comments.value && (
          <div className="timesheet-rejected-comment">
            <Labelbox
              type="textarea"
              labelname="Rejected Comment"
              value={fieldConfiguration.rejected_comments.value || ""}
              disabled
              rows={3}
            />
          </div>
        )}
      </div>

      {/* Excel import / export - only when date range is selected */}
      {isDateRangeSelected && !viewModeRef.current && (
        <div className="timesheet-excel-actions panel">
          <span className="timesheet-excel-actions-label">
            Bulk add from Excel:
          </span>
          <Button
            icon={<GetAppOutlined />}
            text="Download sample Excel"
            handleClick={handleDownloadSampleExcel}
            variant="outline"
            className="timesheet-download-sample-btn"
          />
          <Button
            icon={<CloudUploadOutlined />}
            text="Upload Excel"
            handleClick={() => excelInputRef.current?.click()}
            variant="outline"
            className="timesheet-upload-excel-btn"
          />
          <input
            ref={excelInputRef}
            type="file"
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            onChange={handleUploadExcel}
            style={{ display: "none" }}
          />
        </div>
      )}

      {/* Table Card */}
      <div className="timesheet-table-card panel">
        <div className="timesheet-table-header">
          <div className="timesheet-table-title">Add Items</div>
          {!viewModeRef.current && (
            <div style={{ display: "flex", gap: "12px" }}>
              {/* <Button
                icon={<CloudUploadOutlined />}
                text="Import Excel"
                variant="outline"
                className="timesheet-import-btn"
              /> */}
              <Button
                icon={<AddCircleOutlineOutlined />}
                text="Add"
                handleClick={() => {
                  setSubActivityOptionsByRow((prev) => [...prev, []]);
                  setFieldConfiguration((prev) => ({
                    ...prev,
                    itemDetails: [
                      ...(prev.itemDetails || []),
                      {
                        date: "",
                        billed_type: "Billable",
                        client_id: "",
                        business_name: "",
                        activity_id: "",
                        sub_activity_id: "",
                        description: "",
                        hours: "",
                        status: 1,
                        custom_sub_activity: "",
                      },
                    ],
                  }));
                  setClientInputModeByRow((prev) => [...prev, false]);
                }}
                variant="outline"
                className="timesheet-add-row-btn"
              />
            </div>
          )}
        </div>
        <div className="cc">
          <Table
            aligncss="timesheet-table-scope"
            key={`timesheet-table-${tableUpdateKey}`}
            variant="small"
            title=""
            header={
              !viewModeRef.current
                ? [...headers, { id: "9", label: "Action" }]
                : headers
            }
            data={fieldConfiguration.itemDetails || []}
            fields={defaultFields}
            hiddenFields={[
              "client_contract_item_id",
              "client_invoice_item_id",
              "status",
              "timesheet_item_id",
              "custom_sub_activity", // Hide custom_sub_activity field from table display
            ]}
            actions={!viewModeRef.current ? ["delete"] : null}
            onActionClick={onItemAction}
            validateRef={submitValidateRef}
            cancelValidate={cancelValidateRef}
            initialRowsPerPage={10}
          />
        </div>
      </div>

      {/* Action Buttons */}
      {!viewModeRef.current && (
        <div style={{ paddingBottom: 24 }} className="timesheet-action-buttons">
          <Button
            text="Save"
            large
            handleClick={() => onSubmit(0)}
            className="timesheet-save-btn"
          />
          <Button
            text={`${statusMode.current?.status === 2 ? "Update" : "Submit"}`}
            large
            handleClick={() => onSubmit(1)}
            className="timesheet-submit-btn"
          />
        </div>
      )}
    </div>
  );
};

export default CreatePage;
